import { useEffect, useLayoutEffect, useRef } from 'react';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { SENTRY_ENABLED } from '@/config';
import { useLanguage } from '@/hooks/user';
import { logout } from '@/store/profile/thunks';
import { showError } from '@/utils';
import { isIOSMobile } from '@/utils/device';

let sentryLoader = null;

export function useLanguageConfig() {
	const language = useLanguage();
	const { i18n } = useTranslation();

	useEffect(() => {
		i18n.changeLanguage(language);
	}, [language]);

	return null;
}

export function useYupConfig() {
	const { t, i18n } = useTranslation();

	useLayoutEffect(() => {
		Yup.setLocale({
			mixed: {
				required: t('validations.mixed.required')
			},
			string: {
				min: t('validations.string.min'),
				max: t('validations.string.max'),
				email: t('validations.string.email')
			}
		});

		document.documentElement.lang = i18n.language;

		if (i18n.isInitialized) {
			// data-site-id and data-locale for crawler
			// also used to show the site when translations are ready
			document.body.dataset.siteId = 'pdfe';
			document.body.dataset.locale = i18n.language;
		}
	}, [i18n.language, i18n.isInitialized]);
}

const dayjsLanguageLoader = {
	es: () => import('dayjs/locale/es')
};

export function useDayjs() {
	dayjs.extend(localizedFormat);
	const language = useLanguage();

	useEffect(() => {
		if (language === 'en') {
			dayjs.locale('en');
			return;
		}

		if (dayjsLanguageLoader[language]) {
			dayjsLanguageLoader[language]().then(() => dayjs.locale(language));
		}
	}, [language]);
}

export function useLogoutControl() {
	const dispatch = useDispatch();
	const pendingLogout = useRef();

	useEffect(() => {
		async function handleLogout() {
			if (pendingLogout.current) {
				return;
			}

			pendingLogout.current = true;

			try {
				await dispatch(logout()).unwrap();
			} catch (error) {
				showError(error);
			} finally {
				pendingLogout.current = false;
			}
		}

		window.addEventListener('logout', handleLogout);

		return () => {
			window.removeEventListener('logout', handleLogout);
		};
	}, []);
}

export function usePreventIOSAutozoom() {
	useEffect(() => {
		if (isIOSMobile()) {
			document
				.querySelector('[name=viewport]')
				.setAttribute(
					'content',
					'width=device-width, initial-scale=1, maximum-scale=1'
				);
		}
	}, []);
}

async function loadSentry() {
	try {
		const { initSentry } = await import('@/utils/sentry');
		return initSentry();
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error('Failed to load sentry', err);
	}
}

if (SENTRY_ENABLED) {
	sentryLoader = loadSentry();
}

export function useSentryConfig() {
	const email = useSelector(state => state.profile.user?.email);

	useEffect(() => {
		if (!SENTRY_ENABLED) {
			return;
		}

		(async () => {
			const Sentry = await sentryLoader;
			Sentry?.setUser(email ? { email } : null);
		})();
	}, [email]);
}
